"use client";

import * as React from "react";
import { useState } from "react";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";
import { cva } from "class-variance-authority";
import { Menu, Home, Building, Layers, CreditCard, Mail, ArrowRight } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetClose,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";

const features = [
  {
    title: "Dirección Permanente",
    href: "/#funciones/direccion-permanente",
    description:
      "Obtén una dirección física en México que funcione como tu oficina oficial",
  },
  {
    title: "Escaneo y Digitalización",
    href: "/#funciones/escaneo-digitalizacion",
    description:
      "Recibe notificaciones en tiempo real y visualiza tu correspondencia de manera digital",
  },
  {
    title: "Reenvío de Paquetes",
    href: "/#funciones/reenvio-paquetes",
    description:
      "Gestiona el envío de paquetes y documentos a cualquier ubicación",
  },
  {
    title: "Panel Inteligente",
    href: "/#funciones/panel-de-control",
    description: "Administra todo desde una plataforma centralizada con IA",
  },
];

const planes = [
  {
    title: "Plan Básico",
    href: "/#planes/basico",
    description: "Ideal para emprendedores y pequeños negocios que inician",
  },
  {
    title: "Plan Estándar",
    href: "/#planes/estandar",
    description:
      "Perfect para negocios en crecimiento con necesidades intermedias",
  },
  {
    title: "Plan Premium",
    href: "/#planes/premium",
    description:
      "Solución completa para empresas que requieren máxima flexibilidad",
  },
];

// Custom navigation menu trigger style with LexGuard colors
const lexguardNavigationMenuTriggerStyle = cva(
  "group inline-flex h-9 w-max items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition-colors focus:outline-none disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-transparent text-white hover:bg-lexguard-accent hover:text-white focus:bg-lexguard-accent focus:text-white",
        secondary: "bg-lexguard-secondary text-white hover:bg-lexguard-secondary/90",
        accent: "bg-lexguard-accent2-amber text-white hover:bg-lexguard-accent2-amber/90",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

export function MainNav() {
  const isMobile = useIsMobile();
  const [isOpen, setIsOpen] = useState(false);
  
  const navItems = [
    { href: "/", label: "Inicio", icon: <Home className="h-5 w-5" /> },
    { href: "/empresas", label: "Empresas", icon: <Building className="h-5 w-5" /> },
    { href: "/#caracteristicas", label: "Funciones", icon: <Layers className="h-5 w-5" /> },
    { href: "/#planes", label: "Planes", icon: <CreditCard className="h-5 w-5" /> },
    { href: "/contacto", label: "Contacto", icon: <Mail className="h-5 w-5" /> },
  ];

  return (
    <div className="flex items-center justify-between w-full">
      {/* Logo */}
      <div className="flex items-center">
        <a href="/" className="text-white text-xl font-bold">LexGuard</a>
      </div>
      
      {isMobile ? (
        <div className="flex items-center space-x-2">
          {/* Mobile Navigation */}
          <Sheet onOpenChange={(open) => setIsOpen(open)}>
            <SheetTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon" 
                className="text-white hover:bg-lexguard-accent/20 relative overflow-hidden group"
              >
                <span className="absolute inset-0 bg-gradient-to-r from-lexguard-accent/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                <Menu className="h-6 w-6 relative z-10" />
                <span className="sr-only">Menú</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-gradient-to-b from-lexguard-primary to-lexguard-primary/95 text-white border-lexguard-gray-light/20 p-0 w-full sm:max-w-md">
              <div className="flex flex-col h-full">
                <SheetHeader className="p-6 border-b border-lexguard-gray-light/20">
                  <div className="flex items-center justify-between">
                    <SheetTitle className="text-white text-2xl font-bold">LexGuard</SheetTitle>
                    <div className="h-1 w-12 bg-lexguard-accent rounded-full"></div>
                  </div>
                </SheetHeader>
                
                <div className="flex-1 overflow-auto py-8">
                  <nav className="flex flex-col space-y-2 px-6">
                    {navItems.map((item, index) => (
                      <SheetClose asChild key={item.href}>
                        <a 
                          href={item.href}
                          className={`group relative py-4 px-6 rounded-lg text-white hover:bg-white/10 transition-all duration-300 overflow-hidden opacity-0 transform translate-x-4 ${
                            isOpen ? 'animate-slide-in' : ''
                          }`}
                          style={{ 
                            animationDelay: `${index * 100}ms`,
                            animationFillMode: 'forwards'
                          }}
                        >
                          <span className="absolute inset-0 bg-gradient-to-r from-lexguard-accent/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                          <span className="absolute left-0 top-0 bottom-0 w-1 bg-lexguard-accent transform scale-y-0 group-hover:scale-y-100 transition-transform duration-300 origin-top"></span>
                          <div className="relative flex items-center">
                            <span className="mr-3 text-lexguard-accent/80 group-hover:text-lexguard-accent transition-colors">
                              {item.icon}
                            </span>
                            <span className="text-lg font-medium">{item.label}</span>
                          </div>
                        </a>
                      </SheetClose>
                    ))}
                  </nav>
                </div>
                
                <div className="p-6 border-t border-lexguard-gray-light/20 bg-black/20 backdrop-blur-sm">
                  <div className="flex flex-col space-y-4">
                    <p className={`text-white/70 text-sm mb-2 opacity-0 transform translate-y-4 ${
                      isOpen ? 'animate-fade-in-up' : ''
                    }`}
                    style={{ 
                      animationDelay: `${navItems.length * 100 + 100}ms`,
                      animationFillMode: 'forwards'
                    }}>
                      Acceso a plataformas
                    </p>
                    <SheetClose asChild>
                      <a 
                        href="/app" 
                        className={`w-full py-3 px-4 bg-lexguard-secondary text-white rounded-lg text-center hover:bg-lexguard-secondary/90 transition-colors shadow-lg shadow-lexguard-secondary/20 flex items-center justify-center opacity-0 transform translate-y-4 ${
                          isOpen ? 'animate-fade-in-up' : ''
                        }`}
                        style={{ 
                          animationDelay: `${navItems.length * 100 + 200}ms`,
                          animationFillMode: 'forwards'
                        }}
                      >
                        <span className="text-base font-medium">Aplicación</span>
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </a>
                    </SheetClose>
                    <SheetClose asChild>
                      <a 
                        href="/portal" 
                        className={`w-full py-3 px-4 bg-lexguard-accent2-amber text-white rounded-lg text-center hover:bg-lexguard-accent2-amber/90 transition-colors shadow-lg shadow-lexguard-accent2-amber/20 flex items-center justify-center opacity-0 transform translate-y-4 ${
                          isOpen ? 'animate-fade-in-up' : ''
                        }`}
                        style={{ 
                          animationDelay: `${navItems.length * 100 + 300}ms`,
                          animationFillMode: 'forwards'
                        }}
                      >
                        <span className="text-base font-medium">Portal</span>
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </a>
                    </SheetClose>
                  </div>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      ) : (
        <>
          {/* Desktop Navigation */}
          <NavigationMenu>
            <NavigationMenuList className="flex space-x-1">
              {navItems.map((item) => (
                <NavigationMenuItem key={item.href}>
                  <NavigationMenuLink className={lexguardNavigationMenuTriggerStyle()} asChild>
                    <a href={item.href}>{item.label}</a>
                  </NavigationMenuLink>
                </NavigationMenuItem>
              ))}
            </NavigationMenuList>
          </NavigationMenu>
          
          {/* Auth Links */}
          <div className="flex items-center space-x-2">
            <a href="/app" className={cn(lexguardNavigationMenuTriggerStyle({ variant: "secondary" }))}>
              App
            </a>
            <a href="/portal" className={cn(lexguardNavigationMenuTriggerStyle({ variant: "accent" }))}>
              Portal
            </a>
          </div>
        </>
      )}
    </div>
  );
}

const ListItem = React.forwardRef<
  React.ElementRef<"a">,
  React.ComponentPropsWithoutRef<"a">
>(({ className, title, children, ...props }, ref) => {
  return (
    <li>
      <NavigationMenuLink asChild>
        <a
          ref={ref}
          className={cn(
            "block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-lexguard-accent/10 hover:text-lexguard-primary focus:bg-lexguard-accent/10 focus:text-lexguard-primary",
            className
          )}
          {...props}
        >
          <div className="text-sm font-medium leading-none text-lexguard-primary">{title}</div>
          <p className="line-clamp-2 text-sm leading-snug text-lexguard-gray-medium">
            {children}
          </p>
        </a>
      </NavigationMenuLink>
    </li>
  );
});
ListItem.displayName = "ListItem";
